// Global cache store
const cache = new Map<
  string,
  {
    data: any
    timestamp: number
    isValidating?: boolean
  }
>()

// Store for in-flight requests
const inFlightRequests = new Map<string, Promise<any>>()

// Cache TTL in milliseconds (e.g., 5 minutes)
const DEFAULT_CACHE_TTL = 5 * 60 * 1000

// Generate cache key
const getCacheKey = (endpoint: string, method: string, body: any) => {
  return `${method}:${endpoint}:${body ? JSON.stringify(body) : ''}`
}

// Extract original fetch logic to a separate function
const performFetch = async (
  endpoint: string,
  method: string,
  body: any = null,
  customOptions: any = {},
  parseJson = true
) => {
  try {
    const { headers: customHeaders = {}, ...restOptions } = customOptions

    // Start with default headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...customHeaders, // Merge custom headers
    }

    // Add auth headers conditionally
    if (import.meta.env.VITE_NODE_ENV !== 'willUseHttpOnly') {
      const accessToken = localStorage.getItem('access_token')
      const signinMethod = localStorage.getItem('signin_method')
      if (accessToken) {
        headers.authorization = accessToken
      }
      if (signinMethod) {
        headers.signinmethod = signinMethod
      }
    }

    const options = {
      method,
      headers,
      ...(body && { body: JSON.stringify(body) }),
      ...restOptions,
    }

    // Ensure endpoint starts with the correct server URL if it's a relative path
    const url = endpoint.startsWith('http')
      ? endpoint
      : `${import.meta.env.VITE_SERVER_URL}/api${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`

    const response = await fetch(url, options)

    // Handle token refresh from headers first, regardless of status
    if (import.meta.env.VITE_NODE_ENV !== 'willUseHttpOnly') {
      response.headers.forEach((value, key) => {
        if (key === 'set-access-token') {
          localStorage.setItem('access_token', value)
        }
      })
    }

    if (response.status === 401) {
      // Return a specific structure for 401 errors
      return { error: { status: 401, message: 'Unauthorized' } }
    }

    if (parseJson && !response.ok) {
      // Try to parse error json, otherwise return status text
      const errorData = await response
        .json()
        .catch(() => ({ message: `HTTP error! status: ${response.status} ${response.statusText}` }))
      return { error: errorData }
    }

    if (parseJson) {
      // Return the json data if requested
      return await response.json()
    }

    return response
  } catch (error: any) {
    console.error('Fetch error:', error)
    // Ensure consistent error format
    return { error: { message: error.message || 'Network or parsing error' } }
  }
}

// The enhanced customFetch with caching and request deduplication
export const customFetch = async (
  endpoint: string,
  method: string = 'GET',
  body: any = null,
  options?: {
    skipCache?: boolean
    cacheTTL?: number
    forceRefresh?: boolean
    headers?: Record<string, string>
    [key: string]: any
  },
  parseJson = true
) => {
  // Only use cache for GET requests by default
  const shouldUseCache = !options?.skipCache && method.toUpperCase() === 'GET'
  const cacheTTL = options?.cacheTTL ?? DEFAULT_CACHE_TTL
  const forceRefresh = options?.forceRefresh || false
  const cacheKey = getCacheKey(endpoint, method, body)

  // Return cached data if valid and not forcing refresh
  if (shouldUseCache && !forceRefresh) {
    const cachedResponse = cache.get(cacheKey)
    const now = Date.now()
    const isCacheValid = cachedResponse && now - cachedResponse.timestamp < cacheTTL

    if (isCacheValid) {
      // Handle background revalidation
      if (!cachedResponse.isValidating) {
        // Mark as validating before spawning the background process
        const entry = cache.get(cacheKey)
        if (entry) entry.isValidating = true

        // Use setTimeout to ensure we return cached data immediately
        setTimeout(() => {
          // Check for existing in-flight requests before creating a new one
          if (!inFlightRequests.has(cacheKey)) {
            const revalidatePromise = performFetch(endpoint, method, body, options, parseJson)
              .then((result) => {
                if (!result.error) {
                  cache.set(cacheKey, {
                    data: result,
                    timestamp: Date.now(),
                    isValidating: false,
                  })
                }
                return result
              })
              .catch((err) => {
                const entry = cache.get(cacheKey)
                if (entry) entry.isValidating = false
                throw err
              })
              .finally(() => {
                // Clean up the in-flight request when done
                if (inFlightRequests.get(cacheKey) === revalidatePromise) {
                  inFlightRequests.delete(cacheKey)
                }
              })

            inFlightRequests.set(cacheKey, revalidatePromise)
          }
        }, 0)
      }

      // Return cached data immediately
      return cachedResponse.data
    }
  }

  // Handle fresh fetch with deduplication
  if (inFlightRequests.has(cacheKey) && !forceRefresh) {
    // Reuse existing in-flight request
    const result = await inFlightRequests.get(cacheKey)
    return result
  }

  // Create new request Promise
  const fetchPromise = performFetch(endpoint, method, body, options, parseJson)
    .then((result) => {
      // Cache successful GET responses
      if (shouldUseCache && !result.error) {
        cache.set(cacheKey, {
          data: result,
          timestamp: Date.now(),
        })
      }
      return result
    })
    .finally(() => {
      // Clean up the in-flight request when done
      if (inFlightRequests.get(cacheKey) === fetchPromise) {
        inFlightRequests.delete(cacheKey)
      }
    })

  // Store promise for deduplication
  inFlightRequests.set(cacheKey, fetchPromise)

  // Wait for the result
  return await fetchPromise
}

// Cache management utilities
export const invalidateCache = (endpoint?: string, method: string = 'GET', body?: any) => {
  if (endpoint) {
    const key = getCacheKey(endpoint, method, body)
    cache.delete(key)
    // We don't delete in-flight requests to avoid disrupting ongoing operations
  } else {
    cache.clear() // Clear entire cache
    // We don't clear in-flight requests to avoid disrupting ongoing operations
  }
}
